import { useState } from 'react';
import { useParams, Link } from 'react-router';
import {
  ArrowLeft,
  PieChart,
  Bot,
  History,
  TrendingUp,
  TrendingDown,
  ArrowDownToLine,
  ArrowUpFromLine,
} from 'lucide-react';
import { useMt5Accounts, useAccountHistory } from '@/hooks/use-accounts';
import { useBots } from '@/hooks/use-bots';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { formatCurrency, pnlColor, formatPnl, timeAgo } from '@/lib/format';
import type { AccountHistoryItem } from '@/types/api';
import type { AccountHistoryParams } from '@/hooks/use-accounts';

type PortfolioTab = 'allocations' | 'history';

export default function AccountPortfolioPage() {
  const { mt5AccountId } = useParams();
  const { data: accounts, isLoading: accountsLoading } = useMt5Accounts();
  const { data: bots, isLoading: botsLoading } = useBots({ refetchInterval: 10_000 });
  const [activeTab, setActiveTab] = useState<PortfolioTab>('allocations');

  const isLoading = accountsLoading || botsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const account = (accounts ?? []).find((a) => a.id === mt5AccountId);
  if (!account) {
    return (
      <div className="glass-sm rounded-lg p-6 border-red/20 text-center">
        <p className="text-red text-sm font-semibold mb-2">Account not found</p>
        <Link to="/accounts" className="inline-block mt-3">
          <Button variant="ghost" size="sm">
            <ArrowLeft size={14} />
            Back to Accounts
          </Button>
        </Link>
      </div>
    );
  }

  const accountBots = (bots ?? []).filter(
    (b) => b.mt5Account?.id === mt5AccountId,
  );
  // Count ALL bots (active + stopped) — stopped bots still hold allocated capital
  const totalAllocated = accountBots.reduce((sum, b) => sum + b.assigned_capital, 0);
  const totalPnl = accountBots.reduce((sum, b) => sum + (b.combined_pnl ?? b.total_pnl ?? 0), 0);
  const totalBalance = account.synced_balance ?? 0;
  const unallocated = totalBalance - totalAllocated;

  return (
    <div className="space-y-5">
      {/* Back Link */}
      <Link
        to="/accounts"
        className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-ink-3 hover:text-orange transition-colors"
      >
        <ArrowLeft size={12} />
        All Accounts
      </Link>

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-surface-2 flex items-center justify-center">
          <PieChart size={18} className="text-orange" />
        </div>
        <div>
          <h1 className="text-lg font-extrabold tracking-tight">
            Portfolio — {account.name || `#${account.login_id}`}
          </h1>
          <p className="text-xs text-ink-3">
            {account.login_id} @ {account.server}
          </p>
        </div>
      </div>

      {/* Balance Overview */}
      <div className="glass-sm rounded-lg p-5">
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider mb-1">
              Available
            </p>
            <p className="text-lg font-extrabold font-mono">{formatCurrency(totalBalance, account.currency || 'USD')}</p>
          </div>
          <div>
            <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider mb-1">
              Allocated
            </p>
            <p className="text-lg font-extrabold font-mono text-orange">
              {formatCurrency(totalAllocated, account.currency || 'USD')}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider mb-1">
              Unallocated
            </p>
            <p className={`text-lg font-extrabold font-mono ${unallocated >= 0 ? 'text-green' : 'text-red'}`}>
              {formatCurrency(unallocated, account.currency || 'USD')}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider mb-1">
              Bot P&L
            </p>
            <p className={`text-lg font-extrabold font-mono ${pnlColor(totalPnl)}`}>
              {formatPnl(totalPnl, account.currency || 'USD')}
            </p>
          </div>
        </div>

        {/* Allocation Bar */}
        {totalBalance > 0 && (
          <div className="space-y-2">
            <div className="w-full h-3 rounded-full bg-surface-2 overflow-hidden flex">
              {accountBots
                .filter((b) => b.assigned_capital > 0)
                .map((bot) => {
                const pct = Math.min((bot.assigned_capital / totalBalance) * 100, 100);
                return (
                  <div
                    key={bot.id}
                    className="h-full first:rounded-l-full last:rounded-r-full bg-orange/50"
                    style={{ width: `${pct}%` }}
                    title={`${bot.name}: ${formatCurrency(bot.assigned_capital, account.currency || 'USD')} (P&L: ${formatPnl(bot.combined_pnl ?? bot.total_pnl, account.currency || 'USD')})`}
                  />
                );
              })}
              {unallocated > 0 && (
                <div
                  className="h-full bg-green/30"
                  style={{ width: `${(unallocated / totalBalance) * 100}%` }}
                  title={`Unallocated: ${formatCurrency(unallocated, account.currency || 'USD')}`}
                />
              )}
            </div>
            {/* Legend */}
            <div className="flex items-center gap-4 text-[10px] text-ink-4">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-orange/50" />
                Allocated
              </span>
              {unallocated > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm bg-green/30" />
                  Unallocated
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tab Bar */}
      <div className="flex gap-1 bg-surface/50 rounded-lg p-1">
        <button
          onClick={() => setActiveTab('allocations')}
          className={`flex items-center gap-1.5 text-[10px] font-bold px-3 py-1.5 rounded-md transition-all cursor-pointer flex-1 justify-center ${
            activeTab === 'allocations'
              ? 'bg-orange-bg text-orange'
              : 'text-ink-3 hover:text-ink hover:bg-surface'
          }`}
        >
          <PieChart size={14} />
          Allocations
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex items-center gap-1.5 text-[10px] font-bold px-3 py-1.5 rounded-md transition-all cursor-pointer flex-1 justify-center ${
            activeTab === 'history'
              ? 'bg-orange-bg text-orange'
              : 'text-ink-3 hover:text-ink hover:bg-surface'
          }`}
        >
          <History size={14} />
          History
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'allocations' && (
        <div>
          <h2 className="text-sm font-extrabold mb-3">
            Bot Allocations ({accountBots.length})
          </h2>

          {accountBots.length === 0 ? (
            <div className="glass-sm rounded-lg p-8 text-center">
              <Bot size={32} className="text-ink-4 mx-auto mb-2" />
              <p className="text-xs text-ink-3 mb-3">No bots on this account</p>
              <Link to="/bots/new">
                <Button size="sm">Create Bot</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {accountBots.map((bot) => {
                const pnl = bot.combined_pnl ?? bot.total_pnl;
                return (
                  <Link
                    key={bot.id}
                    to={`/bots/${bot.id}`}
                    className="glass-sm rounded-lg p-3 flex items-center gap-3 hover:border-orange/15 transition-all"
                  >
                    <div className="w-8 h-8 rounded-md bg-surface-2 flex items-center justify-center flex-shrink-0">
                      <Bot
                        size={14}
                        className={bot.is_active ? 'text-green' : 'text-ink-4'}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold truncate">{bot.name}</span>
                        <Badge color={bot.is_active ? 'green' : 'default'}>
                          {bot.is_active ? 'Running' : 'Stopped'}
                        </Badge>
                      </div>
                      <span className="text-[10px] text-ink-4">{bot.symbol}</span>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-bold font-mono">
                        {formatCurrency(bot.assigned_capital, account.currency || 'USD')}
                      </p>
                      <p className={`text-[10px] font-semibold font-mono ${pnlColor(pnl)}`}>
                        {formatPnl(pnl, account.currency || 'USD')}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && mt5AccountId && (
        <AccountHistoryTab mt5AccountId={mt5AccountId} currency={account.currency || 'USD'} />
      )}
    </div>
  );
}

// ============================================================
//  Account History Tab
// ============================================================

function AccountHistoryTab({ mt5AccountId, currency }: { mt5AccountId: string; currency: string }) {
  const [typeFilter, setTypeFilter] = useState<AccountHistoryParams['type']>('all');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useAccountHistory(mt5AccountId, {
    page,
    limit: 50,
    type: typeFilter,
  });

  const items = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="space-y-3">
      {/* Type Filter */}
      <div className="flex gap-1">
        {(['all', 'trades', 'deposits', 'withdrawals'] as const).map((t) => (
          <button
            key={t}
            onClick={() => { setTypeFilter(t); setPage(1); }}
            className={`text-[10px] font-bold px-3 py-1.5 rounded-md transition-all cursor-pointer capitalize ${
              typeFilter === t
                ? 'bg-orange-bg text-orange border border-orange/20'
                : 'bg-surface text-ink-3 border border-glass-border hover:bg-surface-2'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Items */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <div className="glass-sm rounded-lg p-8 text-center">
          <History size={32} className="text-ink-4 mx-auto mb-2" />
          <p className="text-xs text-ink-3">No history found</p>
        </div>
      ) : (
        <>
          <div className="space-y-1.5">
            {items.map((item) => (
              <HistoryItemRow key={item.id} item={item} currency={currency} />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2">
              {page > 1 && (
                <Button variant="ghost" size="sm" onClick={() => setPage((p) => p - 1)}>
                  Prev
                </Button>
              )}
              <span className="text-[10px] text-ink-4 font-mono">
                Page {page} of {totalPages}
              </span>
              {page < totalPages && (
                <Button variant="ghost" size="sm" onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================
//  History Item Row
// ============================================================

function HistoryItemRow({ item, currency }: { item: AccountHistoryItem; currency: string }) {
  const iconConfig: Record<string, { icon: React.ReactNode; bg: string }> = {
    BALANCE_CHANGE: {
      icon: item.amount >= 0
        ? <ArrowDownToLine size={14} className="text-green" />
        : <ArrowUpFromLine size={14} className="text-red" />,
      bg: item.amount >= 0 ? 'bg-green/10' : 'bg-red/10',
    },
    TRADE_CLOSE: {
      icon: item.amount >= 0
        ? <TrendingUp size={14} className="text-green" />
        : <TrendingDown size={14} className="text-red" />,
      bg: item.amount >= 0 ? 'bg-green/10' : 'bg-red/10',
    },
    BOT_DEPOSIT: {
      icon: <ArrowDownToLine size={14} className="text-green" />,
      bg: 'bg-green/10',
    },
    BOT_WITHDRAW: {
      icon: <ArrowUpFromLine size={14} className="text-red" />,
      bg: 'bg-red/10',
    },
  };

  const config = iconConfig[item.type] ?? iconConfig['BALANCE_CHANGE']!;

  return (
    <div className="glass-sm rounded-lg p-3 flex items-center gap-3">
      <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${config.bg}`}>
        {config.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold truncate">{item.description}</p>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-ink-4">{timeAgo(item.timestamp)}</span>
          <Badge
            color={
              item.type === 'TRADE_CLOSE' ? 'blue' :
              item.type === 'BALANCE_CHANGE' ? 'violet' :
              item.type === 'BOT_DEPOSIT' ? 'green' : 'red'
            }
          >
            {item.type === 'TRADE_CLOSE' ? 'Trade' :
             item.type === 'BALANCE_CHANGE' ? 'Balance' :
             item.type === 'BOT_DEPOSIT' ? 'Deposit' : 'Withdraw'}
          </Badge>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <p className={`text-sm font-bold font-mono ${pnlColor(item.amount)}`}>
          {formatPnl(item.amount, currency)}
        </p>
      </div>
    </div>
  );
}
